import { Fragment, FunctionComponent, ReactNode } from 'react';
import { trackFeature } from '@/utils/adobeLaunch';
import { t } from '@/utils/i18n';
import { ToggleSwitch } from '@/components/common/ToggleSwitch';
import { FilterState } from './DesignSelector';

interface FilterToggleProps {
  children: ReactNode;
  value: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}

const FilterToggle: FunctionComponent<FilterToggleProps> = ({
  value,
  onChange,
  disabled,
  children,
}) => (
  <div className="design-filter__toggle">
    <label>
      <span>{children}</span>
      <ToggleSwitch value={value} onChange={onChange} disabled={disabled} />
    </label>
  </div>
);

interface FilterTogglesProps {
  filterState: FilterState;
  onFilterChanged: (filter: FilterState) => void;
  safeSearchToggleEnabled: boolean;
  supportsOnlyVectorDesigns: boolean;
}

export const FilterToggles: FunctionComponent<FilterTogglesProps> = ({
  filterState,
  onFilterChanged,
  safeSearchToggleEnabled,
  supportsOnlyVectorDesigns,
}) => (
  <Fragment>
    <FilterToggle
      value={filterState.showOnlyFreeDesigns}
      onChange={(showOnlyFreeDesigns: boolean) => {
        trackFeature('design-selection-free-design-filter');
        onFilterChanged({ ...filterState, showOnlyFreeDesigns });
      }}
    >
      {t('designSelector.freeDesignToggle')}
    </FilterToggle>

    <hr className="design-filter__separator" />

    <FilterToggle
      value={filterState.safeSearch}
      onChange={(safeSearch: boolean) => {
        trackFeature('design-selection-safe-search');
        onFilterChanged({ ...filterState, safeSearch });
      }}
      disabled={!safeSearchToggleEnabled}
    >
      {t('designSelector.showSafeSearchDesigns')}
    </FilterToggle>

    <hr className="design-filter__separator" />

    <FilterToggle
      value={filterState.vectorDesigns}
      onChange={(vectorDesigns: boolean) => {
        trackFeature(
          supportsOnlyVectorDesigns
            ? 'design-selection-printable-design-filter'
            : 'design-selection-color-design-filter'
        );
        onFilterChanged({ ...filterState, vectorDesigns });
      }}
    >
      {supportsOnlyVectorDesigns
        ? t('designSelector.showSelectableDesigns')
        : t('designSelector.colorChangeable')}
    </FilterToggle>
    {supportsOnlyVectorDesigns && (
      <div className="design-filter__hint">{t('designSelector.showSelectableDesigns.hint')}</div>
    )}
  </Fragment>
);
